// LabView.jsx — the Screen Lab (/lab)
// Birds-eye dev dashboard: open a sandboxed test shift (HARD-BLOCKED while a
// real shift is live), force gate states, drive puppet orders through the
// full pipeline with time-warp, and deep-link into any screen in any state.
//
// The Lab is a data puppeteer, not a mock renderer — every chip writes real
// `_isTestData` Firestore docs and the real screens react over their real
// listeners. See utils/labData.js for the write shapes.

import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";
import { STAFF_THEME as T, FONT } from "../../theme/staff";
import {
  openTestShift, closeTestShift, setShiftGates, getStockArt,
  spawnPuppetOrder, spawnPuppetBatch, setPuppetState,
  spawnStandbyPuppet, simulatePayment,
  createExpiredToken, invalidTokenUrl, createCampaignFixture,
  spawnBasketPuppet,
} from "../../utils/labData";
import { createPreviewToken, countTestData, cleanTestData } from "../../utils/testData";
import TicketPreview from "../../components/TicketPreview";

const STATES = ["queued", "drawing", "cooking", "flipping", "ready_to_deliver", "done", "suspended", "rejected"];
const STATE_LABEL = {
  queued: "Queued", drawing: "Drawing", cooking: "Cooking", flipping: "Flip!",
  ready_to_deliver: "Ready", done: "Done", suspended: "Susp.", rejected: "Reject",
};
const STATE_COLOR = {
  queued: T.gold, drawing: T.blue, cooking: T.orange, flipping: T.pink,
  ready_to_deliver: "#00e5cc", done: T.pink, suspended: "#8a8a8a", rejected: "#c0392b",
};

function open(url) { window.open(url, "_blank", "noopener"); }

export default function LabView({ navigate }) {
  const [activeShifts, setActiveShifts] = useState([]);
  const [puppets, setPuppets] = useState([]);
  const [stockArt, setStockArt] = useState([]);
  const [warp, setWarp] = useState(0); // minutes already spent in target state
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [spawnType, setSpawnType] = useState("custom");
  const [batchCustom, setBatchCustom] = useState(3);
  const [batchFlash, setBatchFlash] = useState(2);
  const [counts, setCounts] = useState(null);
  const [ticketType, setTicketType] = useState("flash");
  const [scanUrls, setScanUrls] = useState({}); // per-type real token URLs — QR becomes phone-scannable
  const [basketId, setBasketId] = useState(null);   // last spawned manifest basket
  const [basketUrl, setBasketUrl] = useState(null); // its manifest URL — re-openable per click (popup blockers allow ONE window per gesture)
  const [basketDoc, setBasketDoc] = useState(null); // live raw doc — the design instrument

  const realShift = activeShifts.find(s => !s._isTestData) || null;
  const testShift = activeShifts.find(s => !!s._isTestData) || null;

  // Live active-shift watch — the strip reshapes as shifts open/close anywhere.
  useEffect(() => {
    const q1 = query(collection(db, "shifts"), where("isActive", "==", true));
    return onSnapshot(q1, snap => setActiveShifts(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  // Live puppet list for the active test shift.
  useEffect(() => {
    if (!testShift?.id) { setPuppets([]); return; }
    const q2 = query(collection(db, "orders"), where("shiftId", "==", testShift.id));
    return onSnapshot(q2, snap => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(o => o._isTestData && !o.trashedAt)
        .sort((a, b) => (a.queueNumber ?? 0) - (b.queueNumber ?? 0));
      setPuppets(rows);
    });
  }, [testShift?.id]);

  useEffect(() => { getStockArt().then(setStockArt).catch(() => setStockArt([])); }, []);

  // Live raw basket doc — watch slots flip as you tap the manifest.
  useEffect(() => {
    if (!basketId) { setBasketDoc(null); return; }
    return onSnapshot(doc(db, "baskets", basketId), snap => setBasketDoc(snap.exists() ? snap.data() : null));
  }, [basketId]);

  async function run(label, fn) {
    setBusy(true); setMsg(null);
    try {
      const r = await fn();
      setMsg({ ok: true, text: typeof r === "string" ? r : label });
    } catch (e) {
      setMsg({ ok: false, text: e?.message || String(e) });
    } finally {
      setBusy(false);
    }
  }

  const randomArt = () => (stockArt.length ? stockArt[Math.floor(Math.random() * stockArt.length)] : null);

  // ── styles ──
  const card = { background: T.panel, border: `1px solid ${T.border}`, borderRadius: "14px", padding: "1rem", marginBottom: "1rem" };
  const h2 = { color: T.gold, fontFamily: FONT.display, fontSize: "0.95rem", fontWeight: 700, margin: "0 0 0.7rem" };
  const chip = (bg, fg = T.bg, disabled = false) => ({
    background: bg, color: fg, border: "none", borderRadius: "8px",
    padding: "0.3rem 0.6rem", fontSize: "0.74rem", fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1,
    fontFamily: FONT.body,
  });
  const linkChip = { ...chip("rgba(74,164,225,0.18)", T.sky), border: `1px solid rgba(74,164,225,0.4)` };
  const subtle = { color: T.muted, fontSize: "0.74rem", lineHeight: 1.45 };

  const gates = testShift ? {
    queue: testShift.isQueueOpen !== false,
    join: !!testShift.livestreamJoinEnabled,
    ctx: testShift.contextMode || "both",
  } : null;

  const catalog = useMemo(() => ([
    {
      label: "Customer order flow",
      note: "Per-puppet 👁 buttons below open /order?t= at that puppet's live state. These chips cover the token-level states. /join paid tiers on a test shift check out against the Square SANDBOX — test card 4111 1111 1111 1111, any CVV / future expiry / any ZIP. No real money moves.",
      actions: [
        { label: "🎫 New custom token", fn: async () => { const r = await createPreviewToken("custom"); open(r.url); } },
        { label: "⚡ New flash token", fn: async () => { const r = await createPreviewToken("flash"); open(r.url); } },
        { label: "✨ New magic token", fn: async () => { const r = await createPreviewToken("magic"); open(r.url); } },
        { label: "⌛ Expired token", fn: async () => { const r = await createExpiredToken(); open(r.url); } },
        { label: "❌ Invalid token", fn: async () => open(invalidTokenUrl()) },
        { label: "🎟 Campaign landing (test)", fn: async () => { const r = await createCampaignFixture(); open(r.url); } },
        { label: "📲 /join (QR / stream tier picker)", fn: async () => open("/join") },
        ...(testShift?.id ? [
          { label: "🏆 Combo order (badges drill)", fn: async () => {
            const r = await spawnPuppetOrder(testShift.id, { comboKey: "collector" });
            open(r.url);
            return "Collector's Kit puppet spawned. Check /queue: gold 🎁 Kit + 🖼 Print badges on the card; deliver it from the hand-off tray to drill the checkpoint; close the shift with it undelivered to see the close guard.";
          } },
          { label: "🏆 Combo upgrade drill (sim payment)", fn: async () => {
            const r = await spawnStandbyPuppet(testShift.id, { orderType: "custom", pendingComboKey: "artist" });
            open(r.url);
            return "Standby custom with Artist-combo intent spawned. Tap 💳 Simulate payment on it below — the promote grants comboKey + addOns exactly like a real paid checkout. (For the REAL money path: /join → 📷 → combo screen → sandbox test card.)";
          } },
          { label: "🧺 Basket manifest (3⚡+1📷)", fn: async () => {
            const r = await spawnBasketPuppet(testShift.id);
            setBasketId(r.basketId); setBasketUrl(r.url); open(r.url);
            return "Basket spawned — raw doc live in the inspector below. Kill the tab and reopen via ↗ in the inspector: nothing is lost.";
          } },
          { label: "🧺 Double-serve drill", fn: async () => {
            const r = await spawnBasketPuppet(testShift.id, { flash: 1, custom: 0 }, "Double-Serve Drill");
            setBasketId(r.basketId); setBasketUrl(r.url); open(r.url);
            return "Tab A is open. Now tap '↗ Open another tab' in the Basket Inspector below for Tab B (browsers only allow one popup per click), then race the submit — one wins, the loser gets 'already claimed'.";
          } },
        ] : []),
      ],
    },
    {
      label: "Staff screens",
      links: [
        ["⚡ Register", "/register"], ["🥞 Queue", "/queue"], ["📷 Camera", "/camera"],
        ["🖌 Studio", "/studio"], ["🖼 Library", "/library?tab=pancakes"], ["📦 Shop Orders", "/shop/orders"],
        ["🎛 Event Config", "/event-config"], ["🖼 Commission upload (preview)", "/commission/preview"],
      ],
    },
    {
      label: "Overlays",
      note: "Preview Hub shows every scene as live tiles; append ?theme=zoofari to any scene URL for theme preview.",
      links: [
        ["🎬 Overlay Preview Hub", "/overlay-previews"],
        ["🏪 Booth scene", "/overlay/booth"], ["🥞 Main (calm)", "/overlay/main"],
        ["🌅 Starting Soon", "/overlay/starting-soon"], ["🌙 Outro", "/overlay/outro"],
        ["☕ Break", "/overlay/break"],
      ],
    },
    {
      label: "Public site",
      note: "Shop checkout: use coupon DANTEST (100% off + free shipping) — $0 orders skip the card form entirely. Confirmation email goes to the address you enter, so use your own.",
      links: [
        ["🏠 Home", "/"], ["📚 Pancakeopedia", "/gallery"], ["📅 Events", "/events"],
        ["🧘 Meditations", "/m"], ["🛍 Shop", "/shop"],
        ...(testShift?.id ? [["📜 Test-shift recap (after close)", `/recap/${testShift.id}`]] : []),
      ],
    },
  ]), [testShift?.id]);

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: FONT.body, padding: "1rem 1.25rem 3rem" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
        <h1 style={{ color: T.gold, fontFamily: FONT.display, fontSize: "1.35rem", fontWeight: 800, margin: 0, flex: 1 }}>
          🧪 Screen Lab
        </h1>
        <button onClick={() => navigate("/menu")} style={{ ...chip("transparent", T.muted), border: `1px solid ${T.border}` }}>← Menu</button>
      </div>

      {/* Status banners */}
      {realShift && (
        <div style={{ background: "rgba(192,57,43,0.18)", border: "1px solid rgba(192,57,43,0.7)", color: "#ff8a80", borderRadius: "12px", padding: "0.7rem 1rem", marginBottom: "1rem", fontWeight: 700, fontSize: "0.85rem" }}>
          ⛔ REAL SHIFT LIVE — “{realShift.eventName || realShift.id}”. The Lab is locked: no test shift can open, and no puppet should touch this shift. Close the real shift first.
        </div>
      )}
      {testShift && !realShift && (
        <div style={{ background: "rgba(252,179,31,0.14)", border: "1px solid rgba(252,179,31,0.55)", color: T.gold, borderRadius: "12px", padding: "0.7rem 1rem", marginBottom: "1rem", fontWeight: 700, fontSize: "0.85rem" }}>
          🧪 TEST SHIFT LIVE — overlays, /register, /queue, and customer links all run against it until you close it here.
        </div>
      )}
      {msg && (
        <div style={{ background: msg.ok ? "rgba(112,190,68,0.12)" : "rgba(192,57,43,0.15)", border: `1px solid ${msg.ok ? "rgba(112,190,68,0.5)" : "rgba(192,57,43,0.6)"}`, color: msg.ok ? "#9be37a" : "#ff8a80", borderRadius: "10px", padding: "0.5rem 0.8rem", marginBottom: "1rem", fontSize: "0.78rem" }}>
          {msg.text}
        </div>
      )}

      {/* ── Test shift control strip ── */}
      <div style={card}>
        <h2 style={h2}>Test Shift</h2>
        {!testShift ? (
          <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", flexWrap: "wrap" }}>
            <button
              disabled={busy || !!realShift}
              onClick={() => run("Test shift opened", () => openTestShift())}
              style={chip(realShift ? "#555" : T.gold, T.bg, busy || !!realShift)}
            >
              🚀 Open Test Shift
            </button>
            <span style={subtle}>
              Creates an `_isTestData` shift linked to a test booking (never appears on the public /events page).
            </span>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.7rem" }}>
              <button disabled={busy} onClick={() => run(gates.queue ? "Queue paused" : "Queue open", () => setShiftGates(testShift.id, { isQueueOpen: !gates.queue }))}
                style={chip(gates.queue ? "#70be44" : T.orange, T.bg, busy)}>
                {gates.queue ? "🟢 Queue OPEN — tap to pause" : "🟠 Queue PAUSED — tap to open"}
              </button>
              <button disabled={busy} onClick={() => run("QR ordering toggled", () => setShiftGates(testShift.id, { livestreamJoinEnabled: !gates.join }))}
                style={chip(gates.join ? T.sky : "#555", T.bg, busy)}>
                {gates.join ? "📲 QR Ordering ON" : "📲 QR Ordering OFF"}
              </button>
              {["stream", "booth", "both"].map(m => (
                <button key={m} disabled={busy || gates.ctx === m} onClick={() => run(`Context: ${m}`, () => setShiftGates(testShift.id, { contextMode: m }))}
                  style={chip(gates.ctx === m ? T.blue : "rgba(255,255,255,0.08)", gates.ctx === m ? T.text : T.muted, busy)}>
                  {m}
                </button>
              ))}
            </div>
            <button disabled={busy} onClick={() => run("Test shift closed — recap builds server-side (flagged _isTestData)", () => closeTestShift(testShift.id))}
              style={chip("rgba(192,57,43,0.85)", "#fff", busy)}>
              🔚 Close Test Shift
            </button>
          </>
        )}
      </div>

      {/* ── Puppet orders ── */}
      <div style={card}>
        <h2 style={h2}>Puppet Orders</h2>
        {!testShift ? (
          <div style={subtle}>Open a test shift to spawn puppets.</div>
        ) : (
          <>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center", marginBottom: "0.8rem" }}>
              <select value={spawnType} onChange={e => setSpawnType(e.target.value)}
                style={{ background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "0.3rem 0.5rem", fontSize: "0.78rem" }}>
                <option value="custom">Custom</option>
                <option value="flash">Flash</option>
                <option value="magic">Magic</option>
              </select>
              <button disabled={busy} style={chip(T.gold, T.bg, busy)}
                onClick={() => run("Puppet spawned", async () => {
                  const opts = { orderType: spawnType };
                  if (spawnType === "flash") { const a = randomArt(); if (a) { opts.designId = a.id; opts.designLabel = a.label; } }
                  await spawnPuppetOrder(testShift.id, opts);
                })}>
                ＋ Spawn puppet
              </button>
              <span style={{ ...subtle, marginLeft: "0.4rem" }}>Batch:</span>
              <input type="number" min="0" max="12" value={batchCustom} onChange={e => setBatchCustom(+e.target.value)}
                style={{ width: "3rem", background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "0.25rem 0.4rem", fontSize: "0.78rem" }} />
              <span style={subtle}>custom ×</span>
              <input type="number" min="0" max="12" value={batchFlash} onChange={e => setBatchFlash(+e.target.value)}
                style={{ width: "3rem", background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "0.25rem 0.4rem", fontSize: "0.78rem" }} />
              <span style={subtle}>flash</span>
              <button disabled={busy} style={chip(T.blue, T.text, busy)}
                onClick={() => run(`Spawned ${batchCustom + batchFlash} puppets`, () => spawnPuppetBatch(testShift.id, { custom: batchCustom, flash: batchFlash }, stockArt))}>
                ＋ Spawn batch
              </button>
              <button disabled={busy} style={chip(T.pink, T.text, busy)}
                onClick={() => run("Standby puppet spawned — 💳 it to drill the paid promote", async () => {
                  const opts = {};
                  const a = randomArt();
                  if (a) { opts.designId = a.id; opts.designLabel = a.label; }
                  await spawnStandbyPuppet(testShift.id, opts);
                })}>
                ＋ Standby puppet
              </button>
              <span style={{ ...subtle, marginLeft: "auto" }}>
                Time-warp:
                <select value={warp} onChange={e => setWarp(+e.target.value)}
                  style={{ marginLeft: "0.35rem", background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "0.2rem 0.4rem", fontSize: "0.76rem" }}>
                  <option value={0}>just now</option>
                  <option value={5}>5 min ago</option>
                  <option value={20}>20 min ago</option>
                  <option value={45}>45 min ago</option>
                </select>
              </span>
            </div>

            {puppets.length === 0 && <div style={subtle}>No puppets yet — spawn one above. State chips write the exact fields the real artist taps write.</div>}

            {puppets.map(p => (
              <div key={p.id} style={{ borderTop: `1px solid ${T.border}`, padding: "0.6rem 0", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 800, color: STATE_COLOR[p.state] || T.text, fontSize: "0.8rem", minWidth: "4.2rem" }}>
                    #{p.queueNumber ?? "—"} {p.queue === "standby" ? "STANDBY" : (STATE_LABEL[p.state] || p.state)}
                  </span>
                  {p.queue === "standby" && (
                    <button disabled={busy} style={chip(T.gold, T.bg, busy)}
                      onClick={() => run("", async () => {
                        const r = await simulatePayment(p.id);
                        return r.promoted
                          ? `💳 Simulated payment OK — ${p.recipientName} promoted to #${r.queueNumber} via the real verify transaction`
                          : `Already on main queue (#${r.queueNumber})`;
                      })}>
                      💳 Simulate payment
                    </button>
                  )}
                  <span style={{ fontSize: "0.82rem" }}>
                    {p.recipientName}{p.subjectName ? ` — ${p.subjectName}` : ""} <span style={subtle}>({p.orderType}{p.designLabel ? `: ${p.designLabel}` : ""})</span>
                  </span>
                  <span style={{ marginLeft: "auto", display: "flex", gap: "0.35rem" }}>
                    <button style={linkChip} onClick={() => open(`/order?t=${p.tokenId}`)}>👁 customer</button>
                    <button style={linkChip} onClick={() => open(`/preview-keepsake/${p.id}`)}>👁 keepsake</button>
                  </span>
                </div>
                <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
                  {STATES.map(s => (
                    <button key={s} disabled={busy || p.state === s}
                      onClick={() => run(`${p.recipientName} → ${STATE_LABEL[s]}`, () => setPuppetState(p, s, { warpMinutes: warp, art: randomArt() }))}
                      style={chip(p.state === s ? STATE_COLOR[s] : "rgba(255,255,255,0.07)", p.state === s ? T.bg : T.muted, busy || p.state === s)}>
                      {STATE_LABEL[s]}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* ── Basket inspector — the manifest design instrument ── */}
      {basketDoc && (
        <div style={card}>
          <h2 style={h2}>🧺 Basket Inspector <span style={{ ...subtle, fontWeight: 400 }}>(raw doc, live — watch slots flip as the manifest is tapped)</span></h2>
          {basketUrl && (
            <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.6rem" }}>
              <button style={linkChip} onClick={() => open(basketUrl)}>↗ Open another tab</button>
              <span style={subtle}>Each tap opens one more manifest tab — open two and race a slot for the double-serve drill.</span>
            </div>
          )}
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.6rem" }}>
            {(basketDoc.slots || []).map((s, i) => (
              <span key={i} style={chip(s.status === "open" ? "rgba(252,179,31,0.25)" : "rgba(112,190,68,0.3)", s.status === "open" ? T.gold : "#9be37a")}>
                {s.ticketType === "custom" ? "📷" : "⚡"} {s.status === "open" ? "open" : `${s.submittedName} #${s.queueNumber}`}
              </span>
            ))}
          </div>
          <pre style={{ background: "rgba(0,0,0,0.35)", border: `1px solid ${T.border}`, borderRadius: "10px", padding: "0.7rem", fontSize: "0.68rem", lineHeight: 1.5, color: T.muted, overflowX: "auto", maxHeight: "260px", margin: 0 }}>
            {JSON.stringify({ ...basketDoc, createdAt: undefined }, (k, v) => (v && v.seconds != null && v.nanoseconds != null) ? "⏱" : v, 2)}
          </pre>
        </div>
      )}

      {/* ── Screen catalog ── */}
      <div style={card}>
        <h2 style={h2}>Screen Catalog</h2>
        {catalog.map(group => (
          <div key={group.label} style={{ marginBottom: "0.9rem" }}>
            <div style={{ color: T.cream, fontWeight: 700, fontSize: "0.8rem", marginBottom: "0.35rem" }}>{group.label}</div>
            {group.note && <div style={{ ...subtle, marginBottom: "0.4rem" }}>{group.note}</div>}
            <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
              {(group.links || []).map(([label, href]) => (
                <button key={href} style={linkChip} onClick={() => open(href)}>{label}</button>
              ))}
              {(group.actions || []).map(a => (
                <button key={a.label} disabled={busy} style={{ ...linkChip, opacity: busy ? 0.5 : 1 }}
                  onClick={() => run(a.label, a.fn)}>{a.label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ── Ticket previews ── */}
      <div style={card}>
        <h2 style={h2}>Ticket Preview <span style={{ ...subtle, fontWeight: 400 }}>(no printer needed — HTML mirror of the thermal layout)</span></h2>
        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.8rem" }}>
          {["flash", "custom", "magic", "promo"].map(t => (
            <button key={t} onClick={() => setTicketType(t)}
              style={chip(ticketType === t ? T.gold : "rgba(255,255,255,0.08)", ticketType === t ? T.bg : T.muted)}>
              {t}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap", marginBottom: "0.8rem" }}>
          <button disabled={busy} style={chip(scanUrls[ticketType] ? "rgba(112,190,68,0.3)" : T.gold, scanUrls[ticketType] ? "#9be37a" : T.bg, busy)}
            onClick={() => run("Scannable token minted", async () => {
              const { url } = ticketType === "promo"
                ? await createCampaignFixture()
                : await createPreviewToken(ticketType);
              setScanUrls(s => ({ ...s, [ticketType]: url }));
              return `QR is live — scan the ${ticketType} ticket from your phone`;
            })}>
            {scanUrls[ticketType] ? "📱 Scannable ✓ — mint fresh" : "📱 Make scannable"}
          </button>
          {scanUrls[ticketType] && (
            <button style={linkChip} onClick={() => open(scanUrls[ticketType])}>↗ Open guest flow</button>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <TicketPreview
            ticketType={ticketType}
            orderUrl={scanUrls[ticketType] || `${window.location.origin}/order?t=lab-preview`}
            expiryDisplay="Expires 8:45 PM"
            serial={ticketType === "promo" ? "JOP-0042" : null}
            tagline={ticketType === "promo" ? "You found one. Breakfast magic awaits." : null}
            subtitle={ticketType === "promo" ? "Scan to reveal your pancake" : null}
          />
        </div>
        <div style={{ ...subtle, marginTop: "0.7rem" }}>
          Mirrors `buildPrintBuffer()` in utils/printer.js — keep the two in sync when ticket layout changes. QR rendered locally (qrcode pkg) at error-correction Q, same as the printed symbol.
          "Make scannable" swaps in a REAL `_isTestData` token (promo = campaign fixture) so scanning the screen with your phone opens the live guest flow — open the test shift first or flash/custom land on the "queue opens soon" holding page. Minted tokens purge with the test-data sweep.
        </div>
      </div>

      {/* ── Cleanup ── */}
      <div style={card}>
        <h2 style={h2}>Cleanup</h2>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <button disabled={busy} style={chip("rgba(255,255,255,0.1)", T.text, busy)}
            onClick={() => run("Counted", async () => { setCounts(await countTestData()); })}>
            🔍 Count test docs
          </button>
          <button disabled={busy} style={chip("rgba(192,57,43,0.85)", "#fff", busy)}
            onClick={() => run("Purged all test data", async () => {
              if (testShift) await closeTestShift(testShift.id);
              const r = await cleanTestData();
              setCounts(null);
              return `Purged: ${Object.entries(r).map(([k, v]) => `${k} ${v}`).join(", ")}`;
            })}>
            🗑 Purge ALL test data
          </button>
          {counts && (
            <span style={subtle}>
              {Object.entries(counts).map(([k, v]) => `${k}: ${v}`).join(" · ")}
            </span>
          )}
        </div>
        <div style={{ ...subtle, marginTop: "0.5rem" }}>
          Same `_isTestData` sweep as the Admin Test Harness — bookings, shifts, summaries, orders, tokens, and test-shift recap docs.
        </div>
      </div>
    </div>
  );
}
